import type { StringKey } from '../strings';

/**
 * 🔒 The What's next banner's rule table (decision 1732): for each document kind
 * and status, the sentence naming the next step and the steps it offers, in the
 * order the banner draws them. It lives here, not in the screen, so the API can
 * read the same rules later.
 *
 * 🔨 A status with no row draws no banner. 🔨 The page hands the banner only the
 * steps its own footer offers at that moment, and the banner draws only those,
 * so it never offers a button the server would refuse (decision 1732).
 */
export const WHATS_NEXT_STEPS = ['send', 'accept', 'convert', 'createPackage', 'createShipment', 'raiseInvoice', 'recordPayment', 'receive', 'raiseBill', 'pay', 'receiveReturn', 'ship', 'newRfq', 'draftQuote', 'markWon', 'markLost', 'openProgress'] as const;
export type WhatsNextStep = (typeof WHATS_NEXT_STEPS)[number];

export interface WhatsNextRule {
  /** The sentence naming the next step. */
  message: StringKey;
  /** The steps the banner may offer, in its own order. */
  steps: readonly WhatsNextStep[];
}

/** The label each step's button reads. */
export const WHATS_NEXT_STEP_LABEL: Readonly<Record<WhatsNextStep, StringKey>> = {
  send: 'whatsNext.step.send',
  accept: 'whatsNext.step.accept',
  convert: 'whatsNext.step.convert',
  createPackage: 'whatsNext.step.createPackage',
  createShipment: 'whatsNext.step.createShipment',
  raiseInvoice: 'whatsNext.step.raiseInvoice',
  recordPayment: 'whatsNext.step.recordPayment',
  receive: 'whatsNext.step.receive',
  raiseBill: 'whatsNext.step.raiseBill',
  pay: 'whatsNext.step.pay',
  receiveReturn: 'whatsNext.step.receiveReturn',
  ship: 'whatsNext.step.ship',
  newRfq: 'whatsNext.step.newRfq',
  draftQuote: 'deal.quote.draft',
  markWon: 'whatsNext.step.markWon',
  markLost: 'deal.action.lost',
  openProgress: 'whatsNext.step.openProgress',
};

/**
 * 🔒 The table. 🔨 The Work Order, the RFQ and the Vendor Credit carry no row
 * (decision 1732): the Work Order's moves live inside its footer tile, an RFQ's
 * next move is made on one vendor's row, and a Vendor Credit has no page.
 */
export const WHATS_NEXT_RULES: Readonly<Record<string, Readonly<Record<string, WhatsNextRule>>>> = {
  Quote: {
    Draft: { message: 'whatsNext.quote.draft', steps: ['send'] },
    Sent: { message: 'whatsNext.quote.sent', steps: ['accept'] },
    ClientViewed: { message: 'whatsNext.quote.sent', steps: ['accept'] },
    Accepted: { message: 'whatsNext.quote.accepted', steps: ['convert'] },
  },
  SalesOrder: {
    Released: { message: 'whatsNext.salesOrder.released', steps: ['createPackage', 'createShipment', 'raiseInvoice'] },
  },
  Invoice: {
    // 🔒 A Draft invoice's next step is Send, the footer's face; Issue stays behind the footer's chevron (decision 2691, after 2630).
    Draft: { message: 'whatsNext.invoice.draft', steps: ['send'] },
    Sent: { message: 'whatsNext.invoice.open', steps: ['recordPayment'] },
    PartiallyPaid: { message: 'whatsNext.invoice.open', steps: ['recordPayment'] },
    Overdue: { message: 'whatsNext.invoice.open', steps: ['recordPayment'] },
  },
  PurchaseOrder: {
    Draft: { message: 'whatsNext.purchaseOrder.draft', steps: ['send'] },
    Approved: { message: 'whatsNext.purchaseOrder.draft', steps: ['send'] },
    Sent: { message: 'whatsNext.purchaseOrder.sent', steps: ['receive', 'raiseBill'] },
    PartlyReceived: { message: 'whatsNext.purchaseOrder.sent', steps: ['receive', 'raiseBill'] },
    Received: { message: 'whatsNext.purchaseOrder.received', steps: ['raiseBill'] },
  },
  Bill: {
    Received: { message: 'whatsNext.bill.open', steps: ['pay'] },
    PartiallyPaid: { message: 'whatsNext.bill.open', steps: ['pay'] },
  },
  CreditNote: {
    PendingReturn: { message: 'whatsNext.creditNote.pendingReturn', steps: ['receiveReturn'] },
  },
  Package: {
    NotShipped: { message: 'whatsNext.package.notShipped', steps: ['ship'] },
  },
};

/**
 * 🔒 What the banner draws for one document: the rule's sentence and the steps
 * the page offers, in the rule's order; null where the status has no rule or the
 * page offers none of its steps, which draws no banner.
 */
export function whatsNext(documentType: string, status: string | null | undefined, offered: readonly WhatsNextStep[]): { message: StringKey; steps: WhatsNextStep[] } | null {
  const rule = status ? WHATS_NEXT_RULES[documentType]?.[status] : undefined;
  if (!rule) return null;
  const steps = rule.steps.filter((step) => offered.includes(step));
  return steps.length === 0 ? null : { message: rule.message, steps };
}

/**
 * 🔒 The Deal's table, beside the documents' (T1272; decision 1935): the ladder's
 * own move for the first open sub-stage, keyed on the sub-stage. RFQ Issued
 * offers New RFQ and Submit Quote offers Draft Quote; the Decision stage, with no
 * sub-stage left, offers Mark Won and Mark Lost. 🔨 A sub-stage with no move of
 * its own, the budget talk among them, offers Open Deal Progress.
 */
export const DEAL_NEXT_RULES: Readonly<Record<string, readonly WhatsNextStep[]>> = {
  RFQIssued: ['newRfq'],
  SubmitQuote: ['draftQuote'],
  Decision: ['markWon', 'markLost'],
};

/**
 * 🔒 The Deal banner's steps: the first open sub-stage's rule, or the Decision
 * stage's where no sub-stage is open, kept to what the page offers; Open Deal
 * Progress where none of them is offered.
 */
export function dealNextSteps(firstSubStage: string | null, stage: string | null, offered: readonly WhatsNextStep[]): WhatsNextStep[] {
  const rule = (firstSubStage ? DEAL_NEXT_RULES[firstSubStage] : stage === 'Decision' ? DEAL_NEXT_RULES.Decision : undefined) ?? [];
  const steps = rule.filter((step) => offered.includes(step));
  if (steps.length > 0) return steps;
  return offered.includes('openProgress') ? ['openProgress'] : [];
}

/** One Sales Order line as its next step reads it: what it ships, what is invoiced, and what a drop-ship order will ship. */
export interface SalesOrderNextLine {
  itemId: string | null;
  isService?: boolean;
  quantity: number;
  quantityConsumed: number;
  invoicedQuantity: number;
  /** 🔒 The part a drop-ship order not yet confirmed ships from the vendor (decision 1930). */
  dropShipAwaiting?: number;
  /** 🔒 A time and materials service line, which its job's invoice bills and the order's never does (decision 2591). */
  billsFromJob?: boolean;
}

/** What a Sales Order has left to do, and the banner's sentence and steps for it (decision 1931). */
export interface SalesOrderNext {
  /** A goods line still has something to pack and ship from our own shelf. */
  shipLeft: boolean;
  /** A line, or a cleared change order's line, still has something to invoice. */
  invoiceLeft: boolean;
  message: StringKey;
  steps: WhatsNextStep[];
  /** The oldest unpaid invoice raised from the order, which Record Payment opens; null where none is. */
  openInvoiceId: string | null;
}

/**
 * 🔒 The Sales Order's next step read off its state, not its status alone
 * (decision 1931, T1257): the footer and the banner read this one answer, so
 * they never disagree. Create Package and Create Shipment stand while a goods
 * line has something left that no drop-ship order covers; Create Invoice while
 * anything is uninvoiced; Record Payment, once both are done, opens the unpaid
 * invoice. 🔨 Null on any status but Released, which keeps the table's rule.
 */
export function salesOrderNext(facts: { status: string; lines: readonly SalesOrderNextLine[]; changeOrderUninvoiced?: boolean; openInvoiceId?: string | null }): SalesOrderNext | null {
  if (facts.status !== 'Released') return null;
  const goods = facts.lines.filter((line) => line.itemId && !line.isService);
  const shipLeft = goods.some((line) => line.quantity - line.quantityConsumed - (line.dropShipAwaiting ?? 0) > 1e-9);
  const fromVendor = goods.some((line) => (line.dropShipAwaiting ?? 0) > 1e-9);
  const invoiceLeft = facts.changeOrderUninvoiced === true || facts.lines.some((line) => line.billsFromJob !== true && line.quantity - line.invoicedQuantity > 1e-9);
  const steps: WhatsNextStep[] = [...(shipLeft ? (['createPackage', 'createShipment'] as const) : []), ...(invoiceLeft ? (['raiseInvoice'] as const) : [])];
  const openInvoiceId = facts.openInvoiceId ?? null;
  const base = { shipLeft, invoiceLeft, openInvoiceId };
  if (shipLeft) return { ...base, message: invoiceLeft ? 'whatsNext.salesOrder.released' : 'whatsNext.salesOrder.shipLeft', steps };
  if (invoiceLeft) return { ...base, message: fromVendor ? 'whatsNext.salesOrder.fromVendor' : 'whatsNext.salesOrder.invoiceLeft', steps };
  if (fromVendor) return { ...base, message: 'whatsNext.salesOrder.vendorShipping', steps: [] };
  return { ...base, message: 'whatsNext.salesOrder.done', steps: openInvoiceId ? ['recordPayment'] : [] };
}
